import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router";
import { namedChainsApi, type NamedChainRow } from "./api/named-chains-client";
import { IdentityPanel } from "./IdentityPanel";
import { StepsEditor } from "./StepsEditor";
import { UsageList } from "./UsageList";
import { VersionHistoryList } from "./VersionHistoryList";
import "../admin.css";

function StepsPanel({
  chain,
  onUpdate,
}: {
  chain: NamedChainRow;
  onUpdate: (updated: NamedChainRow) => void;
}) {
  const [steps, setSteps] = useState(chain.steps);
  const isFirstRender = useRef(true);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(async () => {
      const updated = await namedChainsApi.update(chain.id, { steps });
      onUpdate(updated);
    }, 800);
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [steps, chain.id]);

  return <StepsEditor value={steps} onChange={setSteps} />;
}

type TabId = "identity" | "steps" | "usage" | "history";

const TABS: Array<{ id: TabId; label: string }> = [
  { id: "identity", label: "Identity" },
  { id: "steps", label: "Steps" },
  { id: "usage", label: "Usage" },
  { id: "history", label: "History" },
];

export function NamedChainDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [chain, setChain] = useState<NamedChainRow | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabId>("identity");

  useEffect(() => {
    if (!id) {
      setError("No chain ID provided");
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    namedChainsApi
      .get(id)
      .then((data) => {
        if (!cancelled) {
          setChain(data);
          setError(null);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled)
          setError(err instanceof Error ? err.message : "Failed to load chain");
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (isLoading) {
    return (
      <div className="admin-page">
        <div className="admin-loading">
          <div className="loading-spinner" />
        </div>
      </div>
    );
  }

  if (error || !chain) {
    return (
      <div className="admin-page">
        <div className="admin-error">Error: {error ?? "Chain not found"}</div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-header">
        <Link to="/admin/named-chains" className="admin-back">
          &larr; Named Chains
        </Link>
        <h1 className="admin-title">{chain.name}</h1>
      </div>

      <div className="tab-bar">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            role="tab"
            type="button"
            className={`tab-btn ${activeTab === tab.id ? "tab-btn--active" : ""}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="tab-content" role="tabpanel">
        {activeTab === "identity" && (
          <IdentityPanel chain={chain} onUpdate={(updated) => setChain(updated)} />
        )}
        {activeTab === "steps" && (
          <StepsPanel chain={chain} onUpdate={(updated) => setChain(updated)} />
        )}
        {activeTab === "usage" && <UsageList chainId={chain.id} />}
        {activeTab === "history" && (
          <VersionHistoryList chainId={chain.id} onRollback={(updated) => setChain(updated)} />
        )}
      </div>
    </div>
  );
}
